// ─── TheBurntPeanut Simulator 9000 — Shared Types ──────────────────
// Single source of truth for ALL game modules.
// Every subsystem (map, weapons, enemies, audio, HUD) reads/writes
// through these interfaces. Do NOT define game state elsewhere.
//
// INTEGRATION RULE: When a parallel task delivers a module, it must
// conform to the interfaces here. If it needs new fields on GameState,
// add them here first, then wire them in engine.ts.

import type { PlacedRoom, RoomMap, FloorZone } from './roomMapgen';
import type { Shrine } from './shrines';
import type { WildCardMachine } from './wildCardMachines';
type Chest = WildCardMachine;   // 23.12 compat alias — PSBOT files still say Chest
import type { CharacterDef } from './assets';
import { BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH, BORDER_COLLISION_WEST, BORDER_COLLISION_EAST } from './borderWalls';
import { BOUNDLESS_MAP, MAP_PROPS } from './mapChunks';   // BETA 22.96
import { MAP_FOREST, isForestWalkBlockedAt } from './forest';   // 23.41 G1 / 23.50 forgiving margin
import { propFootprintAt } from './worldProps';   // BETA 23.13
import { isRoomWall, classifyPoint, isRoomGreen, getGreenRedirectTarget, pointInPolygon } from './roomCollisionGrid';
import { isInAnyExteriorPolygon } from './polygonGrid';
import type { ExteriorGreenCurve } from './roomCollisionGrid';

// Re-export for convenience
export type { PlacedRoom, RoomMap, FloorZone } from './roomMapgen';
export type { Shrine } from './shrines';
export type { WildCardMachine } from './wildCardMachines';
export type { Chest } from './chests';   // 23.12 compat re-export

// ─── Core Entities ──────────────────────────────────────────────────

// BETA 23.62 — CO-OP PHASE 1: the game state can now hold 2–4 players.
// Slot ids are fixed 'p1'..'p4' (matches net/protocol.ts, where PlayerId is
// the wire string). Single-player = exactly one entry ('p1').
export type PlayerId = 'p1' | 'p2' | 'p3' | 'p4';

export interface Player {
  // BETA 23.62 — CO-OP PHASE 1 identity + downed state. In single-player
  // these are inert: id='p1', downed stays false, reviveProgress stays 0.
  // The downed/revive MECHANIC arrives in a later phase — fields exist now
  // so every system that copies/creates Player structs carries them.
  id: PlayerId;
  downed: boolean;
  reviveProgress: number;  // 0..1 while a teammate holds revive (co-op only)

  x: number;
  y: number;
  speed: number;
  size: number;            // collision radius
  spriteSize: number;      // visual size
  goopCount: number;
  currentRoom: PlacedRoom | null;
  bobPhase: number;
  facing: number;          // 1 = right, -1 = left

  // ── Fields for future modules (safe defaults set in engine.ts) ──
  hp: number;
  maxHp: number;
  xp: number;
  level: number;
  xpToNext: number;
  invincibleTimer: number; // seconds of i-frames remaining
  // BETA 21.33 — enemy hit-effects ON the player (Dylan: "enemies should hit you
  // and you go flying"). knockbackX/Y = decaying shove velocity applied by enemy
  // contact (scaled by the enemy's attack strength); slowUntil = game-time the
  // player's move-speed slow (from Timmy-type hits) expires.
  knockbackX?: number;
  knockbackY?: number;
  slowUntil?: number;
  gold: number;            // currency for opening chests

  // Computed bonus stats (reset + recalculated each frame by itemsAdapter)
  luck: number;
  critChance: number;
  critDamage: number;
  lifesteal: number;
  shield: number;
  maxShield: number;
  // BETA 16.36 — shield regen rate (HP/sec, applied 5s after last shield
  // damage). Base 1.0, items + shrines add on top.
  shieldRegen: number;
  armor: number;
  evasion: number;
  knockbackBonus: number;
  projectileCountBonus: number;
  rangeBonus: number;           // Flat world-units added to weapon lock-on range
  pickupRange: number;          // % bonus to loot vacuum radius (0 = base, 20 = +20%)
  xpMultiplier: number;
  goldMultiplier: number;
  hpRegen: number;
  damageBonus: number;
  attackSpeedBonus: number;
  thorns: number;
  // BETA 13.77 — newly exposed so the stat-effect tracker can verify these shrines/items.
  /** Flat % bonus damage when the target is an `elite` variant. e.g. 20 = +20% damage to elites. */
  eliteDamageBonus: number;
  /** Flat added to projectile speed across all weapons. Was hardcoded zero in weaponAdapter; now read from here. */
  projectileSpeedBonus: number;
  /** Flat added to projectile size (the existing weapon-system field is `bonusProjectileSize`). Drives AOE size buffs. */
  attackAreaBonus: number;
  /** BETA 13.82 — Flat extra projectile bounces from shrines/items. Drives the `+1 Projectile Bounce` shrine
   *  (was a silent no-op for 15 picks across 8 characters in cycle-3 — no apply case existed). Read in
   *  weaponSystem when projectiles spawn so they bounce more times before despawning. */
  projectileBounceBonus: number;
  /** BETA 19.98 — Flat extra projectile pierce from N08 Not Ritalin perc. Was a dead stat
   *  because weaponSystem.ts read only weapon.projectilePiercing, ignoring this field.
   *  Now bridged via weaponAdapter to PlayerForWeapon.bonusProjectilePiercing. */
  projectilePierceBonus?: number;

  // ── Ultra item system ──
  ultraState?: Record<string, any>;    // per-item runtime state bag
  activeUltraItems?: string[];         // IDs of owned ultra items with onUpdate
  vx?: number;                         // last-frame world velocity (used by taco trail)
  vy?: number;
  damageMultiplier?: number;           // flat multiplier applied on top of damageBonus
  // BETA 13.103 — Not Acid perc. Per-frame chance to phase enemy contact damage.
  // Set by itemsAdapter from accumulated procChance × picks (Not Acid perk).
  // Read by the player.damage() path in engine.ts on each contact event.
  contactPhaseChance?: number;

  // ── BETA 21.11 — Rechargeable dash ──
  // Tap Shift / right-bumper to blink a short burst in the move direction with
  // brief i-frames (reuses invincibleTimer). Two charges, each refills on its
  // own timer. dashActiveTimer > 0 = mid-dash (movement is boosted + trail drawn).
  dashCharges?: number;         // charges currently available (0..DASH_MAX_CHARGES)
  dashRechargeTimer?: number;   // seconds until the next charge refills
  dashActiveTimer?: number;     // seconds of dash-burst remaining (0 = not dashing)
  dashHeldLastFrame?: boolean;  // rising-edge latch so hold ≠ repeat
  dashDirX?: number;            // committed dash direction (latched at fire time)
  dashDirY?: number;
  // BETA 21.18 — shrine-buffable dash stats (computed bonuses, reset+recalc each
  // frame by itemsAdapter, like luck). recharge divides the cooldown; duration
  // multiplies the burst length (= longer dash distance).
  dashRechargeBonus?: number;   // % faster recharge (e.g. 15 = recharge / 1.15)
  dashDurationBonus?: number;   // % longer dash (e.g. 20 = duration × 1.20)
}

export interface GoopBox {
  x: number; y: number;
  roomId: string;
  collected: boolean;
  pulsePhase: number;
  exploding: boolean;
  explodeTime: number;
}

export interface Portal {
  x: number; y: number;
  roomId: string;
  active: boolean;
  pulsePhase: number;
}

export interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  color: string;
  size: number;
  // BETA 20.70 — VFX OVERHAUL Batch A: per-spawn visual identity. The ONE
  // particle renderer used to draw every effect in the game as the same
  // circle (Dylan: "all vfx looks the same"). These optional fields let it
  // draw sparks / shards / rings / motes / bubbles / stars with gravity +
  // spin + stretch — so one upgrade gives dozens of effects their own look.
  // All optional → undefined = the exact legacy circle (zero behavior change).
  shape?: 'circle' | 'spark' | 'shard' | 'ring' | 'mote' | 'bubble' | 'star';
  angle?: number;     // orientation for spark/shard/star (seeded at spawn)
  spin?: number;      // rad/sec angular velocity (shard/star tumble)
  gravity?: number;   // px/sec² added to vy each frame (+ sinks: debris/coins, − rises: gas/embers)
  stretch?: number;   // spark streak length multiplier (default 4)
  drag?: number;      // per-frame velocity retention (default 0.95)
  fadePow?: number;   // alpha curve exponent (default 2 = ease-out)
}

// BETA 20.70 — options for the shape-aware spawnParticles. Omit entirely for
// the legacy omni-circle burst. `dir` set → directional cone; omitted → 360°.
export interface ParticleOpts {
  shape?: Particle['shape'];
  speedMin?: number; speedMax?: number;
  dir?: number; spread?: number;        // dir = base angle (rad), spread = ± cone width
  lifeMin?: number; lifeMax?: number;
  sizeMin?: number; sizeMax?: number;
  gravity?: number; spin?: number; stretch?: number; drag?: number; fadePow?: number;
  spinRandomSign?: boolean;             // randomize tumble direction per particle
}

// ─── Subsystem Interfaces ───────────────────────────────────────────
// Each module exports an object conforming to its interface.
// engine.ts calls these hooks in order every frame.

/** Weapons & Combat module interface */
export interface WeaponsModule {
  /** Called once when the game starts */
  init(state: GameState): void;
  /** Called every frame — update projectiles, check hits, etc. */
  update(state: GameState, dt: number): void;
  /** Called every frame in world-space (after map, before HUD) */
  render(ctx: CanvasRenderingContext2D, state: GameState, viewBounds: ViewBounds): void;
  /** Called when player fires (click/auto) */
  fire?(state: GameState, targetX: number, targetY: number): void;
}

/** Enemies & Waves module interface */
export interface EnemiesModule {
  /** Called once when the game starts */
  init(state: GameState): void;
  /** Called every frame — move enemies, run AI, spawn waves */
  update(state: GameState, dt: number): void;
  /** Called every frame in world-space */
  render(ctx: CanvasRenderingContext2D, state: GameState, viewBounds: ViewBounds): void;
  /** Get all active enemies (for collision checks by weapons module) */
  getEnemies(): Enemy[];
  /** Apply damage to an enemy (called by weapons module).
   *  BETA 23.6x — CO-OP PHASE 2 part C (§3.2): optional trailing sourcePlayerId
   *  = the ATTACKING player (damage/kill attribution thread; the adapter
   *  defaults it to the machine-local player, so SP is byte-identical and every
   *  existing call site compiles unchanged). */
  damageEnemy?(enemyId: number, damage: number, knockback?: { x: number; y: number }, sourcePlayerId?: PlayerId): void;
  /** Spawn a boss at a specific world position (used by boss shrines) */
  spawnBossAt?(definitionId: string, x: number, y: number): number | null;
  /** Returns true if an enemy with the given runtime ID is still alive */
  isEnemyAlive?(instanceId: number): boolean;
  /** Returns the live wave system state (for boss shrine pending count etc.) */
  getWaveState?(): import('./waveSystem').WaveSystemState | null;
  /** BETA 13.70 FPS: O(1) alive enemy count (maintained counter — no filter walk).
   *  Use this instead of `state.enemies.filter(e => e.alive).length` in any per-frame path. */
  aliveCount?(): number;
}

/** Audio & Music module interface */
export interface AudioModule {
  init(): void;
  resume(): void;
  startMusic(): void;
  stopMusic(): void;
  /** Called every frame so music can adapt to intensity */
  update?(state: GameState, dt: number): void;
  // SFX triggers
  playSound(name: string): void;
  setMasterVolume(v: number): void;
  setMusicVolume(v: number): void;
  setSFXVolume(v: number): void;
}

/** Items & GOOP module interface */
export interface ItemsModule {
  init(state: GameState): void;
  update(state: GameState, dt: number): void;
  render(ctx: CanvasRenderingContext2D, state: GameState, viewBounds: ViewBounds): void;
  /** Called when player levels up — returns upgrade choices */
  getLevelUpChoices?(state: GameState): UpgradeChoice[];
  /** Called when player picks an upgrade */
  applyUpgrade?(state: GameState, upgradeId: string): void;
}

// ─── Enemy Type ─────────────────────────────────────────────────────

export interface Enemy {
  id: number;
  type: string;
  // BETA 20.73 — audit fix: variant was dropped in toEngineEnemy, so every
  // boss/elite death logged as ':normal' (e.g. "<boss>:normal" deaths —
  // impossible, mega only ever spawns as variant 'boss'). Death attribution
  // + bot threat logic read this via `(e as {variant?})` fallbacks.
  variant?: 'normal' | 'elite' | 'boss';
  x: number; y: number;
  hp: number; maxHp: number;
  speed: number;
  size: number;
  damage: number;
  alive: boolean;
}

// ─── Upgrade / Level-Up ─────────────────────────────────────────────

export type UpgradeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface StatChange {
  label: string;
  oldValue: string;
  newValue: string;
}

export interface UpgradeChoice {
  id: string;
  name: string;
  description: string;
  icon: string;       // emoji or CDN URL
  rarity: UpgradeRarity;
}

/** Used by LevelUpScreen overlay */
export interface UpgradeCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: UpgradeRarity;
  statChanges?: StatChange[];
  isNew?: boolean;
  level?: number;
}

// ─── Player Combat State (used by UI overlays) ─────────────────────

export interface WeaponInstance {
  slideX?: number;   // BETA 23.01 — weapon smart-slide (see weaponSystem)
  slideY?: number;
  id: string;
  name: string;
  icon: string;
  level: number;
  cooldown: number;
  ammo?: number;
  maxAmmo?: number;
}

export interface PassiveInstance {
  id: string;
  name: string;
  icon: string;
  level: number;
}

// ─── Game Over Stats ────────────────────────────────────────────────

export interface GameOverStats {
  timeSurvived: number;
  enemiesDefeated: number;
  goopCollected: number;
  level: number;
  highestDamage: number;
}

// ─── Audio Settings ─────────────────────────────────────────────────

export interface AudioSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
}

// ─── Rarity Color Map ───────────────────────────────────────────────

// BETA 16.68 — Per Dylan: "these colors are way to similar. can common
// be white? so outlines in white. all text white? and uncommon be that
// green that common is now?" Was: common (#39ff14) and uncommon
// (#4dff4d) — both bright neon greens, visually indistinguishable in
// gameplay. Now: common = white, uncommon = the bright green common
// used to be. Cleaner tier progression white → green → cyan → orange
// → purple as rarity climbs.
// BETA 20.21 — 3-TIER COLORS (Dylan): common GREEN, rare PURPLE, legendary GOLD.
// NO MORE WHITE. uncommon + epic are no longer rolled (see rollUpgradeRarity),
// but they mirror their collapsed tier's color so nothing ever renders
// white / cyan / orange if a stray value slips through.
export const RARITY_COLORS: Record<UpgradeRarity, { border: string; text: string; bg: string; glow: string }> = {
  common:    { border: '#39ff14', text: '#7dff5a', bg: 'rgba(57,255,20,0.07)',  glow: 'rgba(57,255,20,0.45)' },
  uncommon:  { border: '#39ff14', text: '#7dff5a', bg: 'rgba(57,255,20,0.07)',  glow: 'rgba(57,255,20,0.45)' },
  rare:      { border: '#b15cff', text: '#cf9bff', bg: 'rgba(160,60,255,0.07)', glow: 'rgba(160,60,255,0.45)' },
  epic:      { border: '#b15cff', text: '#cf9bff', bg: 'rgba(160,60,255,0.07)', glow: 'rgba(160,60,255,0.45)' },
  legendary: { border: '#ffd700', text: '#ffe680', bg: 'rgba(255,215,0,0.08)',  glow: 'rgba(255,215,0,0.5)' },
};

// BETA 20.44 — removed the dead SAMPLE_UPGRADES demo array (/deep-audit): it was
// imported nowhere and never shown to players (live level-up cards come from
// buildWeaponUpgradeCard in itemsAdapter). It also carried two retired-tier
// 'epic' entries (incendiary_rounds / void_cannon). Deleted to keep the codebase
// free of orphaned content + old rarity tiers.

// ─── View Bounds (passed to render functions) ───────────────────────

export interface ViewBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// ─── Game State ─────────────────────────────────────────────────────
// THE single object that every module reads from and writes to.


export interface GameState {
  phase: 'loading' | 'playing' | 'paused' | 'levelup' | 'shrine_choice' | 'chest_spin' | 'chest_choice' | 'ultra_chest' | 'death_replay' | 'gameover' | 'won';

  // ── BETA 23.62 — CO-OP PHASE 1: multi-player-capable state ──
  // `players` is the one true roster (single-player = { p1 } only). The
  // object itself is STABLE for the life of a run: mutate entries, never
  // replace the container — modules may hold references into it.
  // `player` below is a LIVE ACCESSOR (getter/setter defined in engine.ts
  // state creation) aliasing players[localPlayerId] — same object, so every
  // existing read AND write through state.player keeps working unchanged.
  // (Getter, not a copy: see the 22.02 knockback lesson — writes to a
  // throwaway player copy silently no-op.)
  players: Partial<Record<PlayerId, Player>>;
  localPlayerId: PlayerId;   // whose input/camera/UI this machine owns
  hostPlayerId: PlayerId;    // whose machine runs the true world (SP: same)

  player: Player;
  camera: { x: number; y: number; zoom: number };
  keys: Set<string>;
  mouse: { x: number; y: number; worldX: number; worldY: number; down: boolean };

  debug: boolean;
  startTime: number;
  loadProgress: number;
  interactTarget: 'goop' | 'portal' | 'chest' | 'shrine' | 'barrel' | 'chick_magnet' | 'ultra_chest' | 'boss_shrine' | 'extraction_port' | null;   // 22.98 — 'cloakzy' removed (streamer purge)
  interactPromptAlpha: number;
  interactPromptText: string;

  // BETA 22.98 — Cloakzy NPC state DELETED (streamer likeness purge).

  // ── Extraction port (BETA 22.21 — Tarkov-style escape hatch) ──
  // Opens at 120s; walk onto it + press X to ARM a 60s timer that then runs no
  // matter where you go. Survive to 0 → run ends as 'extracted' (keep scrap /
  // cases / modded guns). null until it spawns; armedAt is the state.time of the
  // X-press (null = not armed); extracted flips the gameover screen to a win.
  extractionPort?: { x: number; y: number; pulsePhase: number } | null;
  extractionArmedAt?: number | null;
  extracted?: boolean;
  showMinimap: boolean;
  // BETA 20.74 — CINEMATIC RUN INTRO (Dylan: "an awesome transition and a
  // zoom out of the map and it zoom in to my character. more of a vibe").
  // Seconds elapsed in the intro camera glide; undefined = intro finished
  // (or never started). Set to 0 at state creation; engine's camera block
  // eases zoom + position from a wide map reveal into the player, then
  // clears the field.
  _introZoomT?: number;
  // Captured on the intro's first frame so the glide lands on the
  // device-correct base zoom (desktop 1.38 / touch 1.0 / handheld 1.7).
  _introEndZoom?: number;
  // BETA 20.74 — HOLD-TO-INSPECT FULL MAP (Dylan: "hit tab to show full
  // map large centered ... when i let go it goes away. same with
  // controller. right trigger"). Two separate fields so the gamepad's
  // per-frame poll can't stomp a held keyboard Tab. Render shows the big
  // centered map while EITHER is true (playing phase only).
  _bigMapKeyHeld?: boolean;
  _bigMapPadHeld?: boolean;
  // BETA 21.89 — when you pause WHILE a pick screen (level-up / shrine / chest) is open,
  // remember that screen so resuming returns to it instead of skipping the pick.
  _prePausePhase?: string;
  // BETA 16.82 — Per Dylan: "give me the UI togle. the UI togle should
  // just toggle the stats and chat on the left." Y button (button 3 on
  // Xbox) toggles this in-game while phase === 'playing'. When true,
  // chat panel + left stats panel skip rendering so cards / shrine /
  // chest screens have unobstructed space. Right panel (weapons/percs)
  // is unaffected per Dylan's spec.
  hudHidden: boolean;
  /** BETA 19.16 — HUD-pass writes the measured chat-panel bottom Y here
   *  so renderLeftStatsPanel can dock directly below the chat instead of
   *  using a hardcoded y=200 that wrapped-multiline chat would spill into.
   *  Zero (or unset) means "no chat rendered this frame; use the default
   *  panelY." Lives on state so the layout phases (HUD pass writes,
   *  stats-panel pass reads) communicate without a new global. */
  _hudChatBottomY?: number;
  /** BETA 19.18 — HUD-pass writes the actual height the top health strip
   *  consumed this frame (HP bar 22px + shield bar 5px when maxShield>0).
   *  The post-overlay chat re-render path (level-up / shrine / chest)
   *  reads this to position the chat panel at the SAME y as the main HUD
   *  pass — was previously hardcoded `topY = 60` which caused the chat
   *  to jump 17–22px down when a level-up opened. */
  _hudTopStripH?: number;
  time: number;
  particles: Particle[];

  // ── World objects ──
  goopBoxes: GoopBox[];
  portal: Portal | null;

  // ── Room-based map data (set during init, read by all) ──
  map: {
    roomMap: RoomMap;
    seed: number;
    level: 1 | 2 | 3;
    WORLD_SIZE: number;
    ROOM_SIZE: number;
  };

  // BETA 13.29: corridorTriangles field removed — 3-zone collision + getDoorwayMagnet
  // replaced the corridor-junction triangle steering entirely.

  // ── Wave / difficulty tracking ──
  wave: number;
  waveTimer: number;
  killCount: number;

  // ── Chest tracking ──
  chestsOpenedThisRun: number;
  // ── Shrine tracking ──
  // BETA 16.90 — PSBOT cycle-30f Bug 6 + cycle-30k §11B: was always 0 in
  // run telemetry even though pickEvents had 587 shrine-source picks.
  // Increments in registerShrineSelectTracker each time a shrine buff
  // is chosen. Surfaced via run.shrinesActivated in livePlayTracker
  // finalize.
  shrinesActivatedThisRun: number;
  // BETA 16.91 — Adrenaline Pod (UC03) reactive-combat timer. Set to
  // state.time + 4.0 in enemyAdapter on damage taken if player has
  // UC03 equipped. Read in applyAllBonusesToPlayer to add +12% atk
  // speed per UC03 stack while the timer is in the future.
  _adrenalineReactiveUntil?: number;

  // ── UI preferences ──
  hudScale: number;       // Icon grid scale factor. Default 1.0, range 0.75–2.0
  rerollsLeft: number;    // Rerolls remaining this run (starts at 3, shared across all screens)

  // ── Screen shake ──
  screenShakeTimer: number;     // Seconds remaining on current shake
  screenShakeIntensity: number; // Max pixel offset during shake

  // ── BETA 13.103 — Per-run accumulators for new percs ──
  /** Total gold spent across the run (chests, shrines, etc.). Drives Debt
   *  perk scaling: +1 maxHp per pick per 500g spent. Reset on initGame. */
  _runGoldSpent?: number;
  /** Seconds remaining since the last damage event taken by the player.
   *  Drives Not Xanax perc condition (recently_damaged: true while > 0).
   *  Set to 3.0 in player.damage() path; ticks down each frame. */
  _postDamageTimer?: number;
  /** True after Liar Deceiver has fired its decoy revive this run. Mirrors
   *  the existing _sheeeshUsedThisRun pattern. Reset on initGame. */
  _liarUsedThisRun?: boolean;

  // ── BETA 15.14 — N10 Debt mechanic state (gold-as-health-bar) ──
  /** Per-frame snapshot of the engine's _debtActiveUntil (game-time when
   *  the Debt window expires). Set in engine update each frame so the
   *  enemyAdapter player.damage callback can read it without a circular
   *  import. Game-time = state.time. */
  _debtActiveUntilSnapshot?: number;
  /** Per-frame snapshot of the player's Debt slot accumulatedBonus. Drives
   *  the per-hit gold-cost discount: cost% = max(5, 25 - 0.5 × accumulated). */
  _debtAccumulatedSnapshot?: number;
  /** Total Debt activations this run (= number of times player hit 0 HP and
   *  Debt fired before Sheeesh/Liar). Reset on initGame. */
  _debtSavesTriggeredThisRun?: number;
  /** Total gold spent across all Debt windows this run (sum of all per-hit
   *  gold deductions during Debt-active state). */
  _debtGoldSpentThisRun?: number;
  /** Total enemy hits absorbed by gold (instead of HP) during Debt windows
   *  this run. Each redirected hit increments this. */
  _debtHitsAbsorbedThisRun?: number;
  /** Times Debt force-ended early because gold reached 0 mid-window. After
   *  this fires the next hit lands normally → Last Stand path takes over. */
  _debtForceEndedThisRun?: number;

  // ── BETA 15.15 — N13 Not Xanax (slow-on-hit-taken) state ──
  /** Per-frame snapshot of N13 perk slot's accumulatedBonus. Drives slow
   *  duration: 1.0 + 0.05 × accumulated, capped at 3.0s. */
  _notXanaxAccumulatedSnapshot?: number;
  /** Total times an enemy was slowed by N13 (each contact hit triggers
   *  one slow application on the attacking enemy). */
  _notXanaxSlowsAppliedThisRun?: number;
  /** Sum of slow-frame durations applied by N13 across all contact events. */
  _notXanaxTotalSlowSecondsThisRun?: number;

  // ── BETA 15.15 — N06 Let's Ride XXX (bungulator summon) state ──
  /** Per-frame count of perk-spawned bungulators (Math.floor(slot.level / 8),
   *  max 5). Used by engine bungulator spawn loop to expand wantCount. */
  _perkBungulatorCountSnapshot?: number;
  /** Total perk-source bungulators ever spawned this run (matches
   *  _perkBungulatorCountSnapshot once threshold is met; doesn't decrement). */
  _perkBungulatorsSpawnedThisRun?: number;
  /** Mystery Meat Stew per-run accumulated random bonuses. Each kill rolls
   *  one of (maxHp +0.5 / damageBonus +0.5 / luck +0.1) per stack. */
  _mysteryMeatBonuses?: { maxHp: number; damageBonus: number; luck: number };
  // BETA 13.103 — perc-eval reads state.enemies (already populated elsewhere)
  // for conditional perc gates (Bungulazine swarm, Let's Ride alone). No new
  // declaration needed here — state.enemies type comes from autoPlaytester's
  // existing reference.

  // ── BETA 14.21 — Per-attack outcome counters (enemy → player) ──
  // User observation: chad surrounded by ~30 zombies + mini-bosses, full
  // HP, no damage taken — "Enemies are not actually hitting me when they should
  // be." These 5 counters expose what's already happening in the damage callback
  // so the bot CSV (BETA 14.22 next) can compute hit rate, evasion proc rate,
  // iframe block rate, phase chance proc rate, and damage-per-landed-hit. Reset
  // on initGame. Incremented in enemyAdapter.ts player.damage callback.
  /** Total enemy attack attempts this run (every time player.damage callback
   *  fires, regardless of outcome). */
  _enemyAttacksAttemptedThisRun?: number;
  /** Subset of attempts that actually applied HP damage (passed all defensive
   *  checks: not iframe, not phased, not evaded, not fully shielded). */
  _enemyAttacksHitThisRun?: number;
  /** Subset of attempts blocked by player.evasion roll. */
  _enemyAttacksMissedByEvasion?: number;
  /** Subset of attempts blocked by player.invincibleTimer > 0 (i-frames). */
  _enemyAttacksMissedByIframe?: number;
  /** Subset of attempts blocked by Not Acid's contactPhaseChance roll. */
  _enemyAttacksMissedByPhase?: number;
  /** BETA 14.30 (BUG-05) — 5th outcome category. Catch-all for attack
   *  attempts blocked by reasons other than the 3 named ones (e.g. shield
   *  fully absorbed, armor-zero, knockback breaking contact mid-attack,
   *  enemy stun/freeze). attempted should equal hit + 4 missed counters. */
  _enemyAttacksMissedByOther?: number;

  // ── BETA 14.82 — Sprite-touch-vs-damage tracking ──
  // User: "enemy sprites are touching bot and no damage being dealt. that
  // needs to 100% be tracked." Three new counters answer: how often does
  // the visual show a touch but no damage actually happens?
  /** Frames where ANY enemy's SPRITE radius overlapped player's sprite
   *  radius. Visual definition (looser than hitbox). */
  _enemySpriteOverlapFrames?: number;
  /** Frames where sprite overlapped BUT the hitbox didn't (sprite extends
   *  beyond hitbox visually — gap between visual touch and damage gate). */
  _enemySpriteOnlyTouchFrames?: number;
  /** Frames where enemy is in HITBOX touch range but contactCooldownRemaining
   *  is gating the attack (recent successful hit, waiting for next). High
   *  values are NORMAL — this is intended cooldown behavior. Spike values
   *  vs hits-per-sec ratio = bug indicator. */
  _enemyTouchCooldownGated?: number;

  // ── BETA 14.30 — Telemetry rebuild Phase 1: event counters ──
  /** # of revive events fired this run (Sheeesh + Liar Deceiver + Last Stand combined). */
  _reviveEventsThisRun?: number;
  /** # of HP-gain events this run (lifesteal + item heal + kill heal + regen ticks). */
  _healEventsThisRun?: number;
  /** # of evasion-roll-success events this run (player POV; mirrors enemyAttacksMissedByEvasion). */
  _dodgeEventsThisRun?: number;
  /** # of Not Acid phase-chance proc events this run. */
  _phaseEventsThisRun?: number;
  /** # of enemy-knockback events this run (one per knockback application, not per pixel). */
  _knockbackEventsThisRun?: number;

  // ── BETA 14.30 — Telemetry rebuild Phase 1: stat snapshots ──
  // Captured at finalizeRun() / per-frame for live-snapshot analysis.
  /** Final %damage bonus at run end. */
  finalDamagePct?: number;
  finalCritChancePct?: number;
  finalCritDamagePct?: number;
  finalAttackSpeedPct?: number;
  finalMovementSpeedPct?: number;
  finalLifestealPct?: number;
  finalEvasionPct?: number;
  finalPhaseChancePct?: number;
  finalPickupRangePct?: number;
  finalLockOnRange?: number;
  finalKnockbackPct?: number;
  finalEliteDamagePct?: number;
  finalThornsPct?: number;
  finalXPGainPct?: number;
  finalGoldGainPct?: number;
  finalMaxHP?: number;
  finalHPRegen?: number;
  finalArmor?: number;
  finalShield?: number;
  finalOverheal?: number;
  finalProjectileCount?: number;
  finalProjectileBounce?: number;
  finalProjectilePierce?: number;

  // ── BETA 14.30 — Telemetry rebuild Phase 1: stat-delta event log ──
  /** Capped buffer (200 entries; oldest dropped). Append every time a stat
   *  changes via shrine/item/perc/levelup/chest. Bot CSV serializes as JSON. */
  _statDeltaEventsThisRun?: Array<{
    t: number; source: 'shrine' | 'item' | 'perc' | 'levelup' | 'chest';
    sourceId: string; stat: string; before: number; after: number; delta: number;
  }>;

  /** BETA 14.30 — Final stats by source breakdown.
   *  Computed at finalizeRun() from _statDeltaEventsThisRun. */
  _finalStatsBySourceThisRun?: Record<string, { fromShrines: number; fromItems: number; fromPercs: number }>;

  // ── BETA 14.31 (Phase 2) — per-perc / per-item / per-shrine trackers ──
  /** Per-perc state map. Keys are perc IDs (T01-T14, N01-N14). Bot CSV JSON. */
  _percTrackerThisRun?: Record<string, {
    applies: number; currentStack: number; conditionMetFrames?: number;
    currentEffectiveBonus?: number; procCount?: number;
  }>;
  /** Per-item state map. Keys are item IDs. Bot CSV JSON. */
  _itemTrackerThisRun?: Record<string, {
    applies: number; currentStack: number; procCount?: number;
    dmgDealt?: number; statusFramesAttributed?: number;
    qPressCount?: number; qReadyFrames?: number;
  }>;
  /** Per-shrine-buff-type state map. Bot CSV JSON. */
  _shrineTrackerThisRun?: Record<string, { applies: number; totalDelta: number }>;

  // ── BETA 14.32 (Phase 3) — floor-effect tracker + per-frame sweeps ──
  /** Per-floor-effect state map. Bot CSV JSON. */
  _floorEffectTrackerThisRun?: Record<string, {
    spawns: number; activeFramesAvg: number; lifetimeAvgMs: number;
    enemyTriggers: number; dmgDealt: number; statusFramesAttributed: number;
    playerStoodInFrames: number; kills: number; maxStack: number;
  }>;
  _playerHpBucketLowFrames?: number;
  _playerHpBucketMidFrames?: number;
  _playerHpBucketHighFrames?: number;
  _playerStationaryFrames?: number;
  _playerDiagonalMoveFrames?: number;
  _noEnemyNearbyFrames?: number;
  _5plusEnemiesIn150wuFrames?: number;
  _playerMSAvgSnapshot?: number;
  _phaseChanceCurrentAvgSnapshot?: number;
  _critChanceCurrentAvgSnapshot?: number;
  // Ugly Kitten special tracking
  _kittenPlacementsCount?: number;
  _kittenActiveAtAnyTimeAvg?: number;
  _kittenAttacksFiredTotal?: number;
  _kittenDmgDealtTotal?: number;
  _kittenKillsTotal?: number;
  _kittenFreezeAppliedFrames?: number;
  _kittenLifetimeAvgMs?: number;
  _kittenDeathRate?: number;
  _kittenPlacementCoordsBucketed?: { corner: number; center: number; scatter: number };

  // ── BETA 14.33 (Phase 4) — weapon × enemy matrix + synergy ──
  /** Per-(weapon × enemy-type) attribution map. Bot CSV JSON. */
  _weaponEnemyMatrixThisRun?: Record<string, Record<string, {
    shots: number; hits: number; crits: number; dmg: number;
  }>>;
  _topWeaponVsZombieThisRun?: string;
  _topWeaponVsTimmyThisRun?: string;
  _topWeaponVsCaseohThisRun?: string;   // 23.39 — PSBOT-compat column, always '' (CaseOh purged)
  _topWeaponVsEliteThisRun?: string;
  _weakestWeaponOverallThisRun?: string;
  /** Per-synergy-pair state map. Bot CSV JSON. */
  _synergyTrackerThisRun?: Record<string, {
    bothEquipped: boolean; triggerCount?: number; valueDelta?: number;
  }>;

  // ── BETA 14.34 (Phase 5) — event log + death context + near-miss ──
  /** Capped event buffer (200 entries; oldest dropped). Bot CSV JSON. */
  _eventLogThisRun?: Array<{
    t: number;
    type: 'pick' | 'shrine' | 'levelup' | 'death' | 'revive' | 'heal' | 'kill_named_elite' | 'wave_change' | 'chest_open' | 'q_press';
    data: Record<string, unknown>;
  }>;
  /** Captured if a death occurred this run. Bot CSV JSON. */
  _deathContextThisRun?: {
    hpAtDeath: number; fatalHitSource: string; fatalHitDmg: number;
    last5HitsBeforeDeath: Array<{ t: number; source: string; dmg: number }>;
    distanceToNearestEnemy: number; enemiesInContactRangeAtDeath: number;
    iframeStateAtDeath: number;
  };
  _nearMissEventsCount?: number;
  _nearMissEventsLog?: Array<{
    t: number; hpAtNadir: number; recoveryTime: number;
    recoverySource: 'lifesteal' | 'item_heal' | 'natural_regen' | 'overheal' | 'caseoh_cooking';
  }>;
  _firstBloodTime?: number;
  _firstDamageTakenTime?: number;
  _longestStretchBetweenHitsMs?: number;
  _engineFramePerfThisRun?: {
    spikes: Array<{ t: number; frameTimeMs: number }>;
    avgFrameTimeMs: number;
    worstFrameTimeMs: number;
    gcPauseEvents?: Array<{ t: number; durationMs: number }>;
  };

  // ── Last Stand (one save from death per run) ──
  lastStandAvailable: boolean;  // True until first trigger
  lastStandFlashTimer: number;  // Counts down from 1.0 — drives full-screen red flash

  // ── Viewport dimensions (set each frame by game loop) ──
  // Used by weapon system to compute actual on-screen targeting range.
  viewportW: number;
  viewportH: number;

  // ── Character info ──
  character: CharacterDef;

  // ── Portal discovery ──
  portalDiscovered: boolean;

  // ── Minimap fog-of-war ──
  minimapFog: Float32Array;       // 150×150 grid, 0.0 = hidden, 1.0 = revealed
  minimapFogSize: number;         // grid dimension (150)
  minimapRevealRadius: number;    // reveal radius in world units
  // ── Loot orbs (XP gems, gold coins, health pickups) ──
  lootOrbs: import('./lootOrbs').LootOrbState | null;
  // ── Pickup notifications (floating text) ──
  // BETA 16.55 — PSBOT cycle-30m: optional fontSize + position overrides
  // for high-impact events (Sheeesh / Last Stand / Liar Deceiver). The
  // default render path stays unchanged (top-right, 13px) for normal pickups.
  pickupNotifications: {
    text: string;
    color: string;
    timer: number;
    fontSize?: number;          // default 13
    position?: 'top_right' | 'screen_center'; // default 'top_right'
    initialTimer?: number;       // for fade calculations
  }[];

  // ── Ultra loot system ──
  enemies: Enemy[];                    // snapshot from enemiesModule.getEnemies(), updated each frame
  pendingEnemyDamage: Array<{id: number; amount: number; knockbackX?: number; knockbackY?: number}>;
  ultraChest: { x: number; y: number; collected: boolean } | null;

  // ── Quest / unlock notifications + horde warnings (slide-up banners) ──
  runUnlockNotifs: Array<{
    label: string;         // e.g. "Laxative" or "ZOMBIE RUSH"
    type: 'perc' | 'horde'; // 'perc' = teal quest banner, 'horde' = red warning
    timer: number;         // counts DOWN from 5.0 → 0
    slideProgress: number; // 0 → 1 (drives slide-up animation, ~0.15s)
  }>;
  // History of all unlocks gained during this run — shown in pause menu
  runUnlocksHistory: Array<{ label: string; type: 'perc' }>;

  // ── Final boss fight ──
  finalBossActive: boolean;          // Portal entered, final boss fight in progress
  finalBossEnemyIds: number[];       // IDs of the final boss enemies (win when all dead)

  // ── Death screen animation ──
  gameOverStartTime: number;         // Date.now() when gameover phase started
  damageTakenThisRun: number;        // Accumulated damage taken (tracked in enemyAdapter)
  // BETA 13.68: track who killed the player so the gameover screen can show "Killed by X"
  killedByType?: string;             // enemy.type at moment of fatal blow (e.g. 'zombie', 'timmy')
  killedByVariant?: string;          // 'normal' | 'elite' | 'boss'
  killedByName?: string;             // human-readable display name (e.g. 'Crowned Zombie')
  // BETA 22.48 — DEATH CLARITY (Dylan: "i never see an enemy hit me when i die.
  // its very confusing. is it possible to have code to know if i was actually
  // supposed to die"). Computed at the lethal frame so the death screen can
  // explain WHAT killed you, HOW (contact / ranged / slam / DoT), and WHEN.
  deathExplanation?: string;         // plain-English line, e.g. "Zombie hit you for 24 (contact)"
  deathWasPhantom?: boolean;         // true = no FRESH enemy hit at death (stale/unknown source → suspicious)
  fatalHitAgeSec?: number;           // seconds between the last recorded HP-drop and the death frame (-1 = none)
}

// ─── Collision Helpers (shared by all modules) ──────────────────────
// 3-Zone Collision System:
//   RED   = wall panels + corner clips → ALWAYS blocked
//   BLUE  = center cross + tapered doorways → ALWAYS walkable
//   GREEN = curved corners inside rooms → walkable (entities get redirected
//           toward nearest doorway when player is NOT in the room)
//
// Exterior green curves (convex corners, straight walls, concave pockets,
// diagonal bridges) are walkable zones outside rooms that guide entities
// around walls. They are ALWAYS walkable — entities inside them get
// redirected toward the nearest room doorway.

/**
 * Check if a point is blocked by any room wall or barricade.
 * Returns true if the point is walkable (not blocked).
 *
 * In the 3-zone system:
 *   - RED zones → blocked (return false)
 *   - BLUE zones → always walkable (return true)
 *   - GREEN zones → always walkable (return true)
 *     (GREEN redirect behavior is handled separately by getGreenZoneRedirect)
 *
 * @param isEntity - If true, exterior green curves also block (for bot/enemies).
 *                   Player movement should pass false or omit this parameter.
 */
// BETA 14.52 — Room spatial index. DevTools profile (with sourcemaps) showed
// isWalkable as 10.0% of total CPU during heavy gameplay (4,269ms self-time
// across a 65s recording). Each call iterated ALL rooms in the map (~30+),
// checking barricades + bbox + classifyPoint. Math: 50 enemies × 5 corner-
// checks per move (canMoveTo wraps 5 isWalkable calls) × 30 rooms = ~7,500
// room iterations per frame, ~450K/sec at 60fps. With spatial index, each
// query checks only the 1-2 rooms whose bbox contains that grid cell —
// ~30× reduction in per-call work. Same accuracy.
const ROOM_SPATIAL_CELL_SIZE = 1024; // world-units; rooms are typically 768wu
let _roomSpatialCells: PlacedRoom[][][] | null = null;
let _roomSpatialIndexedFor: RoomMap | null = null;
let _roomSpatialGridSize = 0;

function ensureRoomSpatialIndex(state: GameState): void {
  if (_roomSpatialCells && _roomSpatialIndexedFor === state.map.roomMap) return;
  const rooms = state.map.roomMap.rooms;
  const gridSize = Math.ceil(state.map.WORLD_SIZE / ROOM_SPATIAL_CELL_SIZE);
  const cells: PlacedRoom[][][] = [];
  for (let y = 0; y < gridSize; y++) {
    const row: PlacedRoom[][] = [];
    for (let x = 0; x < gridSize; x++) row.push([]);
    cells.push(row);
  }
  for (const room of rooms) {
    const minCx = Math.max(0, Math.floor(room.x / ROOM_SPATIAL_CELL_SIZE));
    const maxCx = Math.min(gridSize - 1, Math.floor((room.x + room.size - 1) / ROOM_SPATIAL_CELL_SIZE));
    const minCy = Math.max(0, Math.floor(room.y / ROOM_SPATIAL_CELL_SIZE));
    const maxCy = Math.min(gridSize - 1, Math.floor((room.y + room.size - 1) / ROOM_SPATIAL_CELL_SIZE));
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        cells[cy][cx].push(room);
      }
    }
  }
  _roomSpatialCells = cells;
  _roomSpatialIndexedFor = state.map.roomMap;
  _roomSpatialGridSize = gridSize;
}

function getCandidateRooms(state: GameState, px: number, py: number): PlacedRoom[] | null {
  ensureRoomSpatialIndex(state);
  if (!_roomSpatialCells) return null;
  const cx = Math.floor(px / ROOM_SPATIAL_CELL_SIZE);
  const cy = Math.floor(py / ROOM_SPATIAL_CELL_SIZE);
  // BETA 14.68 — NaN guard. Without this, NaN coords (which can sneak in from
  // enemy state corruption, divide-by-zero, etc.) cause `_roomSpatialCells[NaN]
  // = undefined` and the next subscript throws "Cannot read properties of
  // undefined (reading 'NaN')". Bot diagnostic log evidence:
  // "Uncaught TypeError: Cannot read properties of undefined (reading 'NaN')"
  // across 5+ characters. NaN comparisons all return false so the existing
  // bounds check fell through. Now: explicit isFinite check, fallback to all
  // rooms (safe).
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  if (cx < 0 || cx >= _roomSpatialGridSize || cy < 0 || cy >= _roomSpatialGridSize) return null;
  return _roomSpatialCells[cy][cx];
}

export function isWalkable(state: GameState, px: number, py: number, isEntity = false): boolean {
  const { WORLD_SIZE } = state.map;

  // Out of world bounds — per-side collision at the "black line" (inner wall edge)
  // BETA 22.96 — the boundless map has NO borders (and no rooms — the room
  // loop below no-ops on the empty array). Machines below still collide.
  if (!BOUNDLESS_MAP) {
    if (px < BORDER_COLLISION_WEST)  return false;
    if (px >= WORLD_SIZE - BORDER_COLLISION_EAST)  return false;
    if (py < BORDER_COLLISION_NORTH) return false;
    if (py >= WORLD_SIZE - BORDER_COLLISION_SOUTH) return false;
  }

  // BETA 22.95 — Wild Cards MACHINES are solid for the PLAYER (Dylan: "they
  // need collision"). Enemies (isEntity) still pass through so pathing can't
  // wedge them. The blocked rect covers the cabinet's lower body, matching
  // the drawn sprite (bottom-anchored at chest.y + 40, 2.35× tall, ~0.71
  // aspect → ~134wu wide). Coarse early-out keeps the loop ~free; the player
  // interacts from CHEST_INTERACT_RADIUS (140), outside this rect.
  // BETA 23.17 — machine/prop feet collision MOVED OUT of isWalkable into
  // playerFeetBlocked(): canMoveTo probes a 5-point BOX around the player,
  // but doc 13's model is "the PLAYER collides with his FEET POINT". Running
  // the feet check on box CORNERS inflated every footprint by the box radius
  // and caught corners during diagonal approach — Dylan's "i get stuck and
  // it flickers" video. canMoveTo now runs the feet check exactly once, on
  // the true feet point. (Side effect, accepted: projectiles no longer die
  // on the 38wu base strip of machines — they already flew over cabinets.)

  // Check rooms — 3-zone polygon collision.
  // A point is walkable if ANY overlapping room says it's not RED.
  // BETA 14.52 — query spatial index for candidate rooms (typically 1-2)
  // instead of iterating all rooms in the map.
  let insideAnyRoom = false;
  let walkableInAnyRoom = false;
  const candidateRooms = getCandidateRooms(state, px, py) ?? state.map.roomMap.rooms;
  for (const room of candidateRooms) {
    // BETA 16.53 — Crushed rooms (the old mega boss belly-flopped) lose ALL
    // wall + barricade collision. The room becomes free open space.
    // Skip every collision check for crushed rooms; flag as walkable
    // when query point is inside the bbox.
    if (room.crushed) {
      const localX = px - room.x;
      const localY = py - room.y;
      if (localX >= 0 && localX < room.size && localY >= 0 && localY < room.size) {
        insideAnyRoom = true;
        walkableInAnyRoom = true;
      }
      continue;
    }
    // BETA 14.64 — Bbox prefilter: skip the barricade loop entirely if the
    // query point is far outside this room's expanded bbox. Profile evidence
    // (post-spatial-index): barricade-loop at 8.4ms self-time inside
    // isWalkable. Barricades are at room edges; if (px, py) is more than
    // 64wu outside the room bbox, no barricade in that room can match.
    // 64wu margin generous enough to cover doorway-extending barricades.
    const _bbMargin = 64;
    const _outsideX = px < room.x - _bbMargin || px >= room.x + room.size + _bbMargin;
    const _outsideY = py < room.y - _bbMargin || py >= room.y + room.size + _bbMargin;
    if (!_outsideX && !_outsideY) {
      // Barricades still use rects (they block doorways at map edges)
      for (const bar of room.barricades) {
        if (px >= bar.x && px < bar.x + bar.w && py >= bar.y && py < bar.y + bar.h) {
          return false;
        }
      }
    }
    const localX = px - room.x;
    const localY = py - room.y;
    if (localX >= 0 && localX < room.size && localY >= 0 && localY < room.size) {
      insideAnyRoom = true;
      const zone = classifyPoint(localX, localY);
      if (zone !== 'red') {
        walkableInAnyRoom = true;
      }
    }
  }
  // If inside at least one room bounding box, walkable only if at least one room says so
  if (insideAnyRoom) return walkableInAnyRoom;

  // ── Exterior green curves = IMPASSABLE for entities (bot + enemies) ──
  // When isEntity=true, exterior green curve polygons block movement,
  // forcing bot/enemies to navigate through doorways instead of cutting corners.
  // Player (isEntity=false) can freely walk through exterior green zones.
  //
  // BETA 13.91 FPS — User's Performance recording showed `pointInPolygon`
  // at 82% of frame budget. Replaced the per-call O(C × V) polygon iteration
  // with an O(1) precomputed-grid lookup (lazy build at first call after a
  // new level loads). Was 9M polygon-vertex tests/sec — now is 0.
  if (isEntity) {
    if (isInAnyExteriorPolygon(state.map.roomMap?.exteriorGreenCurves, WORLD_SIZE, px, py)) {
      return false;
    }
  }

  // Not inside any room and not in any exterior green curve = open space = walkable
  return true;
}

/**
 * Check if an entity at world position (px, py) is in a GREEN zone
 * (either an interior green corner or an exterior green curve).
 * If so, returns a redirect target (world coords) pointing toward
 * the nearest blue doorway. Returns null if not in any green zone.
 *
 * Used by enemy AI and bot to smoothly navigate around corners
 * instead of getting stuck on walls.
 */
export function getGreenZoneRedirect(
  state: GameState, px: number, py: number,
): { x: number; y: number } | null {
  // ── Check interior green zones (inside rooms) ──
  // BETA 14.52 — query spatial index instead of all rooms (same hot path as
  // isWalkable; called by enemy AI per-frame for green-zone redirects).
  const candidateRooms = getCandidateRooms(state, px, py) ?? state.map.roomMap.rooms;
  for (const room of candidateRooms) {
    const localX = px - room.x;
    const localY = py - room.y;
    if (localX >= 0 && localX < room.size && localY >= 0 && localY < room.size) {
      if (isRoomGreen(localX, localY)) {
        return getGreenRedirectTarget(localX, localY, room.x, room.y);
      }
    }
  }

  // ── Check exterior green curves (outside rooms) ──
  // BETA 13.91 FPS — fast-reject via polygon-membership grid first. When the
  // point isn't in ANY polygon (the common case), we skip the O(C × V) loop
  // entirely. When the grid says "yes", we still iterate to find the specific
  // polygon (needed for the redirect target).
  const curves = state.map.roomMap.exteriorGreenCurves;
  if (curves && isInAnyExteriorPolygon(curves, state.map.WORLD_SIZE, px, py)) {
    for (const curve of curves) {
      if (pointInPolygon(px, py, curve.polygon)) {
        // Redirect toward the nearest doorway of the associated room
        // Use the room's local coordinate system to find the nearest doorway
        const localX = px - curve.roomWorldX;
        const localY = py - curve.roomWorldY;
        return getGreenRedirectTarget(
          // Clamp to room bounds for the redirect calculation
          Math.max(0, Math.min(ROOM_SIZE_PX_CONST - 1, localX)),
          Math.max(0, Math.min(ROOM_SIZE_PX_CONST - 1, localY)),
          curve.roomWorldX, curve.roomWorldY,
        );
      }
    }
  }

  return null;
}

/** Room size constant for redirect calculations */
const ROOM_SIZE_PX_CONST = 768;

/**
 * BETA 23.17 — the doc-13 feet check, run ONCE per move on the true feet
 * point (never on canMoveTo's box corners — that inflated footprints and
 * corner-caught the player: "i get stuck and it flickers").
 * Returns true when the player's FEET at (x, y+footOff) land inside a
 * machine or prop footprint — with the already-inside escape hatch.
 */
export function playerFeetBlocked(state: GameState, x: number, y: number): boolean {
  const _plr = state.player;
  const _footOff = (_plr.spriteSize || 40) * 0.6;
  const fyAbs = y + _footOff;

  // 23.41 G1 — FOREST WALLS: walkable space is carved out of the woods.
  // ESCAPE HATCH like every other blocker: if the feet are ALREADY inside
  // the blocked field (spawn edge case / future knockback), let them walk out.
  if (MAP_FOREST && BOUNDLESS_MAP) {
    const _fseed = state.map.roomMap.seed ?? 1;
    if (isForestWalkBlockedAt(x, fyAbs, _fseed) && !isForestWalkBlockedAt(_plr.x, _plr.y + _footOff, _fseed)) return true;
  }

  for (const c of state.map.roomMap.chests) {
    if (c.state === 'gone' || c.type === 'elite') continue;
    const dx = x - c.x;
    const fy = fyAbs - c.y;
    if (dx < -42 || dx > 42 || fy < 6 || fy > 38) continue;   // 23.29: machine ×0.8
    // ESCAPE HATCH: already inside (spawned on the spot) → can walk out.
    const pdx = _plr.x - c.x, pfy = (_plr.y + _footOff) - c.y;
    if (pdx >= -42 && pdx <= 42 && pfy >= 6 && pfy <= 38) continue;
    return true;
  }

  if (MAP_PROPS && BOUNDLESS_MAP) {
    const _seed = state.map.roomMap.seed ?? 1;
    const _hitProp = propFootprintAt(x, fyAbs, _seed);
    if (_hitProp) {
      // position-compare, not identity — the prop cache can rebuild between
      // lookups and hand back fresh objects for the same prop (23.16).
      const _already = propFootprintAt(_plr.x, _plr.y + _footOff, _seed);
      if (!_already || _already.x !== _hitProp.x || _already.y !== _hitProp.y) return true;
    }
  }
  return false;
}

export function canMoveTo(state: GameState, x: number, y: number, size: number, isEntity = false): boolean {
  const s = size * 0.7;
  // walls/rooms/borders still use the body box; machines/props use the
  // single feet point (23.17 — see playerFeetBlocked).
  if (!isEntity && playerFeetBlocked(state, x, y)) return false;
  return isWalkable(state, x - s, y - s, isEntity) &&
         isWalkable(state, x + s, y - s, isEntity) &&
         isWalkable(state, x - s, y + s, isEntity) &&
         isWalkable(state, x + s, y + s, isEntity) &&
         isWalkable(state, x, y, isEntity);
}

/**
 * BETA 16.08 — Line-of-sight raycast. Used by aura/arc/orbital weapons
 * (which apply damage based on distance/angle, not via projectiles) to
 * avoid hitting enemies through walls. Per Dylan: "check every weapon.
 * they are not allowed to shoot through or over walls of rooms."
 *
 * Steps along the line ax,ay → bx,by in stepSize-wu increments, returns
 * false on the first non-walkable cell. Step size 16wu matches the
 * projectile wall-kill (finer than wall thickness 96wu so we can\'t
 * miss a wall in the path). Returns true if the entire line is walkable.
 */
export function hasLineOfSight(state: GameState, ax: number, ay: number, bx: number, by: number): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return true; // same point
  const STEP = 16; // wu — finer than wall thickness (96wu) to prevent skip-through
  const steps = Math.max(1, Math.ceil(len / STEP));
  for (let s = 1; s < steps; s++) {
    const t = s / steps;
    if (!isWalkable(state, ax + dx * t, ay + dy * t)) return false;
  }
  return true;
}

// ─── Utility ────────────────────────────────────────────────────────

const MAX_PARTICLES = 250; // Hard cap — beyond this, new particles are dropped

// BETA 16.59 — Wave 4: graphics density mult, registered by graphicsSettings.ts
// at boot via setParticleDensityMultRef(). Default 1.0 (full density).
// Pull-style avoids an import cycle (types.ts is the deepest dep, can't
// import graphicsSettings because graphicsSettings has no reason to live
// on types.ts and the cycle would be brittle).
let _particleDensityMultGetter: (() => number) | null = null;
export function setParticleDensityMultRef(getter: () => number): void {
  _particleDensityMultGetter = getter;
}

export function spawnParticles(
  particles: Particle[], x: number, y: number,
  color: string, count: number, opts?: ParticleOpts
) {
  if (particles.length >= MAX_PARTICLES) return;
  // BETA 16.59 — apply graphics density multiplier. 'off' (mult=0) skips
  // the spawn entirely; 'low' = 33% of requested; 'medium' = 66%; 'high' = 100%.
  const mult = _particleDensityMultGetter ? _particleDensityMultGetter() : 1.0;
  if (mult <= 0) return;
  const scaledCount = Math.max(1, Math.round(count * mult));
  const actualCount = Math.min(scaledCount, MAX_PARTICLES - particles.length);
  // BETA 20.70 — opts default to the EXACT legacy values (50-200 speed,
  // 0.5-1.3 life, 2-6 size, omni 360°) so every existing call is unchanged.
  const o = opts ?? {};
  const sMin = o.speedMin ?? 50, sMax = o.speedMax ?? 200;
  const lMin = o.lifeMin ?? 0.5, lMax = o.lifeMax ?? 1.3;
  const zMin = o.sizeMin ?? 2, zMax = o.sizeMax ?? 6;
  const directional = o.dir !== undefined;
  const spread = o.spread ?? Math.PI; // ± half-cone when directional
  const wantsAngle = o.shape === 'spark' || o.shape === 'shard' || o.shape === 'star';
  for (let i = 0; i < actualCount; i++) {
    const angle = directional
      ? (o.dir as number) + (Math.random() - 0.5) * 2 * spread
      : Math.random() * Math.PI * 2;
    const speed = sMin + Math.random() * (sMax - sMin);
    // BETA 19.25 — single shared random for life so alpha starts at 1.0 and
    // fades cleanly (life === maxLife at spawn). Preserved.
    const lifeSpan = lMin + Math.random() * (lMax - lMin);
    let spin = o.spin;
    if (spin !== undefined && o.spinRandomSign && Math.random() < 0.5) spin = -spin;
    particles.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: lifeSpan,
      maxLife: lifeSpan,
      color,
      size: zMin + Math.random() * (zMax - zMin),
      shape: o.shape,
      angle: wantsAngle ? angle : undefined,
      spin,
      gravity: o.gravity,
      stretch: o.stretch,
      drag: o.drag,
      fadePow: o.fadePow,
    });
  }
}
